import { createModel } from '@rematch/core'
import { REGEX_SERVICE_ID } from '../constants'
import { removeDeviceName } from '@common/nameHelper'
import { graphQLBasicRequest } from '../services/graphQL'
import { selectActiveAccountId } from '../selectors/accounts'
import { selectDeviceModelAttributes } from '../selectors/devices'
import { alphaSort } from '../helpers/utilHelper'
import { RootModel } from '.'

type ISearchState = ILookup<any> & {
  search: ISearch[]
  fetching: boolean
  cloudSearch: boolean
}

const searchState: ISearchState = {
  search: [],
  fetching: false,
  cloudSearch: true,
}

export default createModel<RootModel>()({
  state: { ...searchState },
  effects: dispatch => ({
    async updateSearch(_: void, state) {
      const { total, size } = selectDeviceModelAttributes(state)
      const { membership: member } = state.accounts

      dispatch.search.set({ cloudSearch: total > size || !!member.length })
    },
    async fetch(name: string, state) {
      if (!state.auth.user) return
      if (!state.search.cloudSearch) return

      name = name.trim()
      const isServiceId = REGEX_SERVICE_ID.test(name)

      dispatch.search.set({ fetching: true })

      const id = selectActiveAccountId(state)
      const size = 10
      const accountQuery = `
        account(id: $id) {
          id
          devices(size: $size, name: $name, id: $serviceId) {
            items {
              id
              name
              platform
              owner {
                email
              }
              services {
                name
                id
              }
            }
          }
          networks {
            id
            name
            connections {
              name
              service {
                name
                id
              }
            }
            owner {
              email
            }
          }
        }`

      const response = await graphQLBasicRequest(
        ` query Search($id: String, $size: Int, $name: String, $serviceId: [String!]) {
              login {
                ${accountQuery}
              }
            }`,
        {
          id,
          size,
          name: isServiceId ? undefined : name,
          serviceId: isServiceId ? [name] : undefined,
        }
      )

      if (response === 'ERROR') return
      const search = await dispatch.search.parse({ response, id })
      await dispatch.search.set({ search })
      dispatch.search.set({ fetching: false })
    },

    async parse({ response }: { response: any; id: string }): Promise<ISearch[]> {
      const data = response?.data?.data?.login?.account

      const accountId = data.id
      const devices = data.devices.items
      const networks = data.networks
      const parsedDevices = devices.map(device =>
        device.services.map(service => {
          const serviceName = removeDeviceName(device.name, service.name)
          return {
            accountId,
            serviceName,
            nodeId: device.id,
            nodeName: device.name,
            nodeType: 'DEVICE',
            combinedName: serviceName + ' ' + device.name + ' ' + device.id + ' ' + service.id,
            serviceId: service.id,
            ownerEmail: device.owner.email,
            targetPlatform: device.platform,
          }
        })
      )

      const parsedNetworks = networks.map(network =>
        network.connections.map(connection => {
          const service = connection.service
          return {
            accountId,
            serviceName: connection.name || service.name,
            nodeId: network.id,
            nodeName: network.name,
            nodeType: 'NETWORK',
            combinedName: service.name + ' ' + network.name + ' ' + connection.name,
            serviceId: service.id,
            ownerEmail: network.owner.email,
            targetPlatform: undefined,
          }
        })
      )

      const all = parsedNetworks.concat(parsedDevices).flat()

      // remove duplicates
      const result = all.filter(
        (item, index) => all.findIndex(({ combinedName }) => combinedName === item.combinedName) === index
      )

      console.log('search result', result)
      return result
    },
  }),

  reducers: {
    reset(state: ISearchState) {
      state = { ...searchState }
      return state
    },
    set(state: ISearchState, params: Partial<ISearchState>) {
      Object.keys(params).forEach(key => (state[key] = params[key]))
      return state
    },
  },
})

export function sortSearch(search: ISearch[]): ISearch[] {
  const sorted = search.sort((a, b) => alphaSort(a.nodeName, b.nodeName))
  return sorted || []
}
